// A notification row's title and body in the READER's language (#3769 phase 1,
// decision of record 2026-10-31: every surface spells the sentence from `kind` +
// `payload`). The server's English `title`/`body` stays on the row and is the
// answer whenever this module returns `null`: an old row written before its
// producer carried an argument, a malformed payload, a kind or face not spelled
// here. The fallback is per row and never `⟦key⟧`.
//
// ONE RENDERER PER (KIND, FACE), each over a zod parse of exactly the arguments its
// producer writes (the producer is named on each schema). A kind with several
// producers (`decision_needed`, `task_blocked`) is spelled only for a face whose
// payload is recognisably that producer's. Since phase 2 such a producer stamps
// `face: '<name>'` on its payload, and the literal is the discriminant; the phase-1
// faces predate it, so their schemas are `.strict()` (any other field is another
// producer's) and take that `face` as optional.
//
// A PHASE-2 SINGLE-PRODUCER KIND'S SCHEMA IS `.strict()`, and names EVERY field
// its producer writes today: a payload carrying a field the face does not name is
// not this producer's row (or is a row whose producer changed), so the face falls
// back to the server's text instead of spelling a sentence from arguments it does
// not have. The fields a producer added to its payload AFTER the face existed are
// OPTIONAL — a row written before them still parses and keeps its copy — while a
// field the SENTENCE needs stays required: a row missing it falls back to the
// server's own text, which is the complete sentence in the server's English.
//
// NOT EVERY SINGLE-PRODUCER KIND IS STRICT: `ask_duplicate` and `key_outage`
// (phase 3, `notification-copy-escalations.ts`) parse a plain `z.object`, so a
// payload carrying a field their face does not name still spells. Strictness is
// per face, not a property of the kind — read each schema, never assume it.
//
// Two faces spell only the TITLE (a scouted bug, a coding pause): their body is
// foreign or machine text (the bug's own detail, `agent_mode` commands to paste),
// so the server's body is kept as is.
//
// Foreign text is data, never translated: a repo name, an artifact name, a CI
// conclusion as GitHub spells it, a failing setup step's signature. They are
// interpolated verbatim; a step's signature sits in quotes under a localized lead.

import { ArtifactVisibilitySchema, BLOCKER_EXPIRY_MS } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import { z } from 'zod';
import { ESCALATION_FACES } from './notification-copy-escalations';
import {
  count,
  face,
  type NotificationCopy,
  type Renderer,
  stamp,
  text,
} from './notification-copy-face';
import { STALL_FACES } from './notification-copy-stall';
import type { NotificationRow } from './notifications';
import { since } from './time';

export type { NotificationCopy } from './notification-copy-face';

/**
 * `apps/worker/src/dispatch/ci-assigner.ts` `ciFailedNotification`. The producer
 * also stamps `ci_job_id` (its own row id, for the CI page); the sentence does not
 * need it, and a row written before it keeps its copy.
 */
const ciFailed = face(
  z
    .object({
      ci_job_id: z.string().optional(),
      github_job_id: z.union([count, text]),
      repo: text,
      conclusion: text,
    })
    .strict(),
  (a, tr) => ({
    title: tr('notifications.kind.ci_failed.title', { repo: a.repo }),
    body: tr('notifications.kind.ci_failed.body', {
      job: a.github_job_id,
      repo: a.repo,
      conclusion: a.conclusion,
    }),
  }),
);

/**
 * `apps/api/src/artifacts/store.ts` `shareLinkNotification`. The producer names the
 * artifact's own ids beside its name; only the name is spoken, so the rest are
 * optional (a row written before them still spells) and nullable where the
 * artifact itself is (an unscoped artifact carries no repo, run or task).
 */
const shareLink = face(
  z
    .object({
      artifact_id: z.string().optional(),
      name: text,
      visibility: ArtifactVisibilitySchema.optional(),
      repo_id: z.string().nullable().optional(),
      run_id: z.string().nullable().optional(),
      task_id: z.string().nullable().optional(),
    })
    .strict(),
  (a, tr) => ({
    title: tr('notifications.kind.share_link.title', { name: a.name }),
    body: tr('notifications.kind.share_link.body', { name: a.name }),
  }),
);

/** `apps/worker/src/cron/ops-digest.ts` `opsDigestNotification`: this account's counts. */
const opsDigest = face(
  z
    .object({
      badKeys: count,
      quietBoxes: count,
      stuckTasks: count,
      stalledOnboarding: count,
      keylessAccounts: count,
    })
    .strict(),
  (a, tr) => {
    const parts = [
      a.badKeys > 0 ? tr.plural('notifications.kind.ops_digest.dead_keys', a.badKeys) : null,
      a.quietBoxes > 0
        ? tr.plural('notifications.kind.ops_digest.quiet_machines', a.quietBoxes)
        : null,
      a.stuckTasks > 0
        ? tr.plural('notifications.kind.ops_digest.stuck_tasks', a.stuckTasks)
        : null,
      a.stalledOnboarding > 0
        ? tr.plural('notifications.kind.ops_digest.stalled_onboarding', a.stalledOnboarding)
        : null,
      a.keylessAccounts > 0
        ? tr.plural('notifications.kind.ops_digest.keyless_accounts', a.keylessAccounts)
        : null,
    ].filter((p): p is string => p !== null);
    // All zero names nothing (the tick never sends one); the server's text stands.
    if (parts.length === 0) return null;
    const summary = parts.join(tr('notifications.kind.ops_digest.separator'));
    return {
      title: tr('notifications.kind.ops_digest.title', { summary }),
      body: tr('notifications.kind.ops_digest.body', { summary }),
    };
  },
);

/**
 * `apps/worker/src/notifications/no-model-keys.ts` `planNoModelKeysNotice`. The
 * `episode` names the revocation the hold belongs to (null for an account that never
 * had a key); the sentence does not need it, so a row written before it keeps its
 * copy.
 */
const noModelKeys = face(
  z
    .object({
      heldTasks: count,
      heldAsks: count,
      episode: z.string().nullable().optional(),
    })
    .strict(),
  (a, tr) => {
    const held = [
      a.heldAsks > 0 ? tr.plural('notifications.kind.no_model_keys.asks', a.heldAsks) : null,
      a.heldTasks > 0 ? tr.plural('notifications.kind.no_model_keys.tasks', a.heldTasks) : null,
    ].filter((p): p is string => p !== null);
    return {
      title: tr('notifications.kind.no_model_keys.title'),
      body:
        held.length === 0
          ? tr('notifications.kind.no_model_keys.body')
          : tr('notifications.kind.no_model_keys.body_held', {
              held: held.join(tr('notifications.kind.no_model_keys.separator')),
            }),
    };
  },
);

/**
 * `apps/worker/src/dispatch/seat-cap.ts` `seatCapNotification`. The remedy follows
 * whose ceiling binds (`cap_source`): the account's seats, or the owner's own grant.
 */
const seatCap = face(
  z
    .object({
      cap: count,
      cap_source: z.enum(['account', 'owner']),
      inFlight: count,
      queued: count,
      face: stamp('seat_cap'),
    })
    .strict(),
  (a, tr) => {
    const tasks = tr.plural('notifications.kind.decision_needed.seat_cap.tasks', a.queued);
    const vars = { running: a.inFlight };
    if (a.cap_source === 'owner') {
      const limit = tr.plural('notifications.kind.decision_needed.seat_cap.concurrent', a.cap);
      return {
        title: tr('notifications.kind.decision_needed.seat_cap.title_owner', { tasks, limit }),
        body: tr.plural('notifications.kind.decision_needed.seat_cap.body_owner', a.queued, {
          ...vars,
          limit,
        }),
      };
    }
    const limit = tr.plural('notifications.kind.decision_needed.seat_cap.seats', a.cap);
    return {
      title: tr('notifications.kind.decision_needed.seat_cap.title_account', { tasks, limit }),
      body: tr.plural('notifications.kind.decision_needed.seat_cap.body_account', a.queued, {
        ...vars,
        limit,
      }),
    };
  },
);

/** `apps/worker/src/consumers/repo-visibility.ts` `repoRequiresUpgradeNotification`. */
const repoVisibility = face(
  z.object({ repoId: text, repo: text, face: stamp('repo_visibility') }).strict(),
  (a, tr) => ({
    title: tr('notifications.kind.decision_needed.repo_visibility.title', { repo: a.repo }),
    body: tr('notifications.kind.decision_needed.repo_visibility.body', { repo: a.repo }),
  }),
);

/** `apps/worker/src/cron/repo-env-fault.ts` `repoEnvFaultNotification`. */
const repoEnvFault = face(
  z
    .object({
      blocker_id: text,
      repo: text,
      signature: text,
      boxes: count,
      face: stamp('repo_env_fault'),
    })
    .strict(),
  (a, tr) => ({
    title: tr('notifications.kind.task_blocked.repo_env_fault.title', { repo: a.repo }),
    body: tr.plural('notifications.kind.task_blocked.repo_env_fault.body', a.boxes, {
      signature: a.signature,
    }),
  }),
);

/**
 * `apps/worker/src/babysit/projection-sink.ts` `prMergedNotification`. The
 * producer's `pr_title` is the pull request's own title (null when it has none);
 * the sentence quotes it when it is there and names the number when it is not, so a
 * row written before the producer carried it still spells.
 */
const prMerged = face(
  z
    .object({
      repo: text,
      pr_number: count,
      pr_title: z.string().nullable().optional(),
    })
    .strict(),
  (a, tr) => {
    const vars = { repo: a.repo, pr: a.pr_number };
    const title = a.pr_title?.trim() ?? '';
    const titled = title !== '';
    return {
      title: tr('notifications.kind.pr_merged.title', vars),
      body: titled
        ? tr('notifications.kind.pr_merged.body_titled', {
            ...vars,
            pr_title: title,
          })
        : tr('notifications.kind.pr_merged.body', vars),
    };
  },
);

/** Each scouted bug's source, in words — literal keys, so the coverage scan sees each. */
const BUG_SOURCE = {
  ci: 'notifications.kind.bug_scouted.source_ci',
  sentry: 'notifications.kind.bug_scouted.source_sentry',
  failing_test: 'notifications.kind.bug_scouted.source_failing_test',
} as const;

/**
 * `apps/worker/src/consumers/qa-bug.ts` `bugToNotificationEvent`: the body is the bug's
 * own detail. The producer's `fingerprint` (the dedup identity) and `ref` (where the
 * signal came from) are named so the schema is the producer's own, and are optional:
 * the sentence speaks the bug's title, not them.
 */
const bugScouted = face(
  z
    .object({
      source: z.enum(['ci', 'sentry', 'failing_test']),
      fingerprint: z.string().optional(),
      ref: z.string().optional(),
      title: text,
    })
    .strict(),
  (a, tr, row) => ({
    title: tr('notifications.kind.bug_scouted.title', {
      source: tr(BUG_SOURCE[a.source]),
      bug: a.title,
    }),
    body: row.body,
  }),
);

/**
 * `apps/worker/src/cron/runner-sweep-notifications.ts` `boxOfflineStaleNotification`.
 * The outage's length is `since`'s compact age (`lib/time.ts`) of a moment `stale_ms` ago.
 *
 * WHICH WITNESS SPOKE (`evidence`, the producer's own discriminant): `presence_absent`
 * — the presence read answered and the box's key was absent, so the outage is
 * confirmed; `heartbeat_only` — the read failed and only the check-in column
 * testifies, so the alert says so instead of claiming a key lapsed. A row written
 * before the producer carried `evidence` falls back to the legacy `observer_blind`
 * flag, which said the same thing.
 */
const boxOffline = face(
  z
    .object({
      runner_id: text,
      stale_ms: count,
      step: count,
      observer_blind: z.boolean().optional(),
      evidence: z.enum(['presence_absent', 'heartbeat_only']).optional(),
      hostname: z.string().nullable(),
      last_page: z.boolean(),
    })
    .strict(),
  (a, tr) => {
    const duration = since(new Date(0).toISOString(), a.stale_ms);
    const host = a.hostname?.trim() ?? '';
    const plain =
      host === ''
        ? tr('notifications.kind.box_offline.title', { duration })
        : tr('notifications.kind.box_offline.title_named', { duration, host });
    const blind =
      a.evidence === 'heartbeat_only' || (a.evidence === undefined && a.observer_blind === true);
    const tail = a.last_page
      ? tr('notifications.kind.box_offline.tail_last')
      : tr('notifications.kind.box_offline.tail_backoff');
    const vars = { runner: a.runner_id, duration, tail };
    return {
      title: blind ? tr('notifications.kind.box_offline.unconfirmed', { title: plain }) : plain,
      body: blind
        ? tr('notifications.kind.box_offline.body_blind', vars)
        : tr('notifications.kind.box_offline.body', vars),
    };
  },
);

/** `apps/worker/src/consumers/backlog-import.ts` `backlogImportNotification`. */
const backlogImported = face(
  z
    .object({
      repo: text,
      filed: count,
      existing: count,
      spent: count,
      truncated: z.enum(['ask_budget', 'page_budget']).nullable(),
      askBudget: count,
      pageBudget: count,
      ai_first: z.boolean(),
    })
    .strict(),
  (a, tr) => {
    const onFile = a.filed + a.existing;
    const read =
      onFile > a.spent
        ? tr('notifications.kind.backlog_imported.read_fresh', {
            repo: a.repo,
            on_file: onFile,
            spent: a.spent,
          })
        : tr('notifications.kind.backlog_imported.read', { repo: a.repo, on_file: onFile });
    const bound =
      a.truncated === null
        ? tr('notifications.kind.backlog_imported.bound_complete', { budget: a.askBudget })
        : a.truncated === 'ask_budget'
          ? tr('notifications.kind.backlog_imported.bound_ask_budget', { budget: a.askBudget })
          : tr('notifications.kind.backlog_imported.bound_page_budget', { pages: a.pageBudget });
    const gate = a.ai_first
      ? tr('notifications.kind.backlog_imported.gate_open')
      : tr('notifications.kind.backlog_imported.gate_closed');
    return {
      title: tr.plural('notifications.kind.backlog_imported.title', a.spent, { repo: a.repo }),
      body: tr('notifications.kind.backlog_imported.body', { read, bound, gate }),
    };
  },
);

/**
 * `apps/worker/src/babysit/marker-link.ts` `authorshipMismatchNotification`: a
 * `platform_notice` on the App lane, a `decision_needed` on a member's token.
 */
const authorshipMismatch = face(
  z.object({
    face: z.literal('authorship_mismatch'),
    repo: text,
    pr_number: count,
    expected_login: text,
    author_login: z.string().nullable(),
    lane: z.enum(['user_token', 'app']),
  }),
  (a, tr) => {
    const vars = {
      repo: a.repo,
      pr: a.pr_number,
      expected: a.expected_login,
      author: a.author_login ?? tr('notifications.kind.authorship_mismatch.no_author'),
    };
    return {
      title: tr('notifications.kind.authorship_mismatch.title', vars),
      body:
        a.lane === 'app'
          ? tr('notifications.kind.authorship_mismatch.body_app', vars)
          : tr('notifications.kind.authorship_mismatch.body_user_token', vars),
    };
  },
);

/** `apps/worker/src/cron/repo-quiet-alarm.ts`. */
const repoQuiet = face(
  z.object({
    face: z.literal('repo_quiet'),
    repo: text,
    silent_hours: count,
    oldest_in_flight_hours: count,
  }),
  (a, tr) => {
    const vars = { repo: a.repo, silent: a.silent_hours, oldest: a.oldest_in_flight_hours };
    return {
      title: tr('notifications.kind.decision_needed.repo_quiet.title', vars),
      body: tr('notifications.kind.decision_needed.repo_quiet.body', vars),
    };
  },
);

/** `apps/worker/src/cron/deploy-verdict-gap.ts` `deployVerdictGapNotification`. */
const deployVerdictGap = face(
  z.object({
    face: z.literal('deploy_verdict_gap'),
    repo: text,
    missing_prs: z.array(count).min(1),
  }),
  (a, tr) => {
    const n = a.missing_prs.length;
    const prs = a.missing_prs.map((pr) => `#${String(pr)}`).join(', ');
    return {
      title: tr.plural('notifications.kind.decision_needed.deploy_verdict_gap.title', n, {
        repo: a.repo,
      }),
      body: tr.plural('notifications.kind.decision_needed.deploy_verdict_gap.body', n, {
        repo: a.repo,
        prs,
      }),
    };
  },
);

/** `apps/worker/src/cron/digest-delivery.ts` `weeklySummaryEvent`. */
const weeklySummary = face(
  z
    .object({
      github_login: text,
      repo_count: count,
      action_count: count,
    })
    .strict(),
  (a, tr) => ({
    title: tr('notifications.kind.weekly_summary.title', {
      login: a.github_login,
      actions: tr.plural('notifications.kind.weekly_summary.actions', a.action_count),
      repos: tr.plural('notifications.kind.weekly_summary.repos', a.repo_count),
    }),
    body: tr('notifications.kind.weekly_summary.body', { login: a.github_login }),
  }),
);

/**
 * How long an onboarding run waits for an answer: the worker's
 * `ONBOARDING_ACCESS_WAIT_MINUTES` (`apps/worker/src/fleet/blocker-ingest.ts`), which
 * no package exports; restated here, beside the sentence that says it.
 */
const ONBOARDING_WAIT_MINUTES = 5;

/**
 * `apps/worker/src/fleet/blocker-ingest.ts` `blockerRaisedNotification`. The run's own
 * ask is foreign text: its `summary` is quoted, never translated.
 */
const blocker = face(
  z.object({
    face: z.literal('blocker'),
    repo: z.string().nullable().optional(),
    summary: text,
    onboarding: z.boolean(),
    blocker_kind: text,
  }),
  (a, tr) => {
    const repo = a.repo?.trim() ?? '';
    const vars = {
      summary: a.summary,
      days: Math.round(BLOCKER_EXPIRY_MS / 86_400_000),
      minutes: ONBOARDING_WAIT_MINUTES,
      repo_line:
        repo === ''
          ? tr('notifications.kind.task_blocked.blocker.repo_none')
          : tr('notifications.kind.task_blocked.blocker.repo_line', { repo }),
    };
    return {
      title:
        repo === ''
          ? tr('notifications.kind.task_blocked.blocker.title_no_repo', vars)
          : tr('notifications.kind.task_blocked.blocker.title', { ...vars, repo }),
      body: a.onboarding
        ? tr('notifications.kind.task_blocked.blocker.body_onboarding', vars)
        : tr('notifications.kind.task_blocked.blocker.body_run', vars),
    };
  },
);

/**
 * `packages/domain/src/entities/coding-paused-notice.ts` `codingPausedNotification`.
 * The body is the `agent_mode` commands that re-enable it, machine text to paste: kept.
 * The producer also stamps the repo's id, the rows each switch wrote and the
 * re-enable commands themselves; the sentence speaks the switches and the reverts, so
 * the rest are optional and a row written before them still spells.
 */
const codingPaused = face(
  z
    .object({
      repo: text,
      repo_id: z.string().optional(),
      reverts: z.array(z.unknown()).min(1),
      paused_keys: z.array(z.enum(['worker', 'ci_fixer'])).min(1),
      tripped_by: count,
      created_rows: z.record(z.string(), z.boolean()).optional(),
      re_enable: z.array(z.unknown()).optional(),
    })
    .strict(),
  (a, tr, row) => {
    const keys = new Set(a.paused_keys);
    const switches =
      keys.size === 2
        ? tr('notifications.kind.coding_paused.switches_both')
        : keys.has('worker')
          ? tr('notifications.kind.coding_paused.switches_worker')
          : tr('notifications.kind.coding_paused.switches_ci_fixer');
    return {
      title: tr('notifications.kind.coding_paused.title', {
        switches,
        repo: a.repo,
        reverts: tr.plural('notifications.kind.coding_paused.reverts', a.reverts.length),
      }),
      body: row.body,
    };
  },
);

/**
 * Every (kind, face) spelled, in the order a kind's faces are tried. Exported so the
 * dynamic-key guard (`i18n.test.ts`) walks the same table the bell does.
 */
export const NOTIFICATION_COPY_FACES = {
  ...ESCALATION_FACES,
  ci_failed: { default: ciFailed },
  share_link: { default: shareLink },
  ops_digest: { default: opsDigest },
  no_model_keys: { default: noModelKeys },
  pr_merged: { default: prMerged },
  bug_scouted: { default: bugScouted },
  box_offline: { default: boxOffline },
  backlog_imported: { default: backlogImported },
  weekly_summary: { default: weeklySummary },
  coding_paused: { default: codingPaused },
  decision_needed: {
    ...ESCALATION_FACES.decision_needed,
    ...STALL_FACES.decision_needed,
    seat_cap: seatCap,
    repo_visibility: repoVisibility,
    authorship_mismatch: authorshipMismatch,
    repo_quiet: repoQuiet,
    deploy_verdict_gap: deployVerdictGap,
  },
  platform_notice: {
    ...ESCALATION_FACES.platform_notice,
    authorship_mismatch: authorshipMismatch,
  },
  task_blocked: { repo_env_fault: repoEnvFault, blocker },
} as const satisfies Readonly<Record<string, Readonly<Record<string, Renderer>>>>;

const FACES: Readonly<Record<string, Readonly<Record<string, Renderer>>>> = NOTIFICATION_COPY_FACES;

/** `row` in the reader's language, or `null` for the server's own title and body. */
export function notificationCopy(row: NotificationRow, tr: Translator): NotificationCopy | null {
  const faces = Object.hasOwn(FACES, row.kind) ? FACES[row.kind] : undefined;
  if (faces === undefined || row.payload === null) return null;
  for (const render of Object.values(faces)) {
    const copy = render(row.payload, tr, row);
    if (copy !== null) return copy;
  }
  return null;
}
